import { useCreateTokenClickedEvent } from "@/libs/analytics/analyticsHooks";
import { ProductCard } from "@/src/components/cards/components/ProductCard";
import { isCollectionSolebound } from "../helpers";
import { NFTContract } from "../types";
import { getOnlyVideoSrcFromUrl } from "@/src/helpers/isUrlVideo";
import { MINT_CHAIN_ID } from "@/libs/apis/onboarding/minting/constants";
import useRevealLinkForProductCard from "../hooks/useRevealLinkForProductCard";
import useCurrentUserFavoriteMutation from "../hooks/useCurrentUserFavoriteMutation";
import { useDefendNftFromMissingMetadata } from "../hooks/useDefendNftFromMissingMetadata";
import { noop } from "lodash";

type ProductCardWrapperProps = {
  nft: NFTContract;
  swooshHandle: string;
  isFavorited?: boolean;
  // Temporarily allowing different react query keys to be passed in
  // to update and save data until we can go through and clean up favorites
  keysToUpdate?: string[];
  enabledTokenTransfer?: boolean;
};

const ProductCardWrapper: React.FC<ProductCardWrapperProps> = ({
  nft,
  swooshHandle,
  keysToUpdate,
  isFavorited = false,
  enabledTokenTransfer,
}) => {
  const { defendedLink } = useDefendNftFromMissingMetadata(nft, swooshHandle);

  const onTokenClicked = useCreateTokenClickedEvent(
    nft.collectionSlug,
    nft,
    "profile",
  );

  const { toggleUserFavorite } = useCurrentUserFavoriteMutation({
    tokenId: nft.tokenId,
    contractAddress: nft.contractAddress,
    chainId: parseInt(MINT_CHAIN_ID),
    keys: keysToUpdate,
  });

  const revealLink = useRevealLinkForProductCard(
    nft.collectionSlug,
    nft.metadata.ownedBy,
  );

  return (
    <ProductCard
      key={nft.tokenId}
      imageAlt={nft.metadata.description}
      imageSrc={nft.metadata.image}
      isSolebound={isCollectionSolebound(nft.collectionSlug)}
      link={defendedLink}
      onLinkClick={onTokenClicked}
      swooshHandle={swooshHandle}
      collectionSlug={nft.collectionSlug}
      tokenId={nft.tokenId as string}
      tokenName={nft.metadata.nameWithoutId}
      tokenContractAddress={nft.contractAddress}
      isFavorited={isFavorited}
      isFavoritingDisabled={toggleUserFavorite === undefined}
      onFavoriteClick={() =>
        toggleUserFavorite && toggleUserFavorite(isFavorited)
      }
      videoSrc={getOnlyVideoSrcFromUrl(nft.metadata.animation_url)}
      revealLink={revealLink}
      enabledTokenTransfer={enabledTokenTransfer}
    />
  );
};

export default ProductCardWrapper;
